import styled from '@emotion/styled';
import { toNumber } from 'lodash';
import React, { useMemo, useState } from 'react';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import { Currency } from '@atlassian/cssxp-ccp-types';
import { MetadataType } from '@css-xp/graphtypes/src/generated-types-new';
import Button from '@atlaskit/button/standard-button';
import classNames from 'classnames';
import { TaxItems } from '@atlassian/cssxp-types-billing';
import { FormattedCurrencyV2 } from '@css-xp/base';
import { BillEstimateOrderItemDto } from '@css-xp/common-services/src/api/gateways/ccp-gateways/src/ccp-api-order';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { formatToUSD } from '../../utils/currency-display';

const defaultAllItems: BillEstimateOrderItemDto['items'] = [];

export const PriceDifferenceContent = ({
  isLoading,
  billEstimate,
  linkedEntitlementsBillEstimate = [],
  currentEstimatedBill = 0,
  setGrandTotal,
  renderDetailsAccordion = false,
}: {
  isLoading: boolean;
  billEstimate?: BillEstimateOrderItemDto;
  linkedEntitlementsBillEstimate?: BillEstimateOrderItemDto[];
  currentEstimatedBill?: number;
  setGrandTotal?: (value: number) => void;
  renderDetailsAccordion?: boolean;
}) => {
  const [showDetails, setShowDetails] = useState<boolean>(false);
  billEstimate = billEstimate || ({} as BillEstimateOrderItemDto);
  const finalEstimates: BillEstimateOrderItemDto[] = useMemo(() => {
    if (Object.keys(billEstimate as BillEstimateOrderItemDto).length !== 0) {
      return [{ ...(billEstimate as BillEstimateOrderItemDto) }, ...linkedEntitlementsBillEstimate];
    }
    return [...linkedEntitlementsBillEstimate];
  }, [linkedEntitlementsBillEstimate, billEstimate]);

  const [finalItems, dstItems] = useMemo(() => {
    const finalItemsList: BillEstimateOrderItemDto['items'] = [];
    const dstItemsList: BillEstimateOrderItemDto['items'] = [];
    finalEstimates?.forEach((estimate: BillEstimateOrderItemDto) => {
      estimate?.items?.forEach((item: BillEstimateOrderItemDto['items'][0]) => {
        if (item?.metadata?.type === MetadataType.ADMIN_FEE) {
          dstItemsList.push(item);
        } else {
          finalItemsList.push(item);
        }
      });
    });
    return [finalItemsList, dstItemsList];
  }, [finalEstimates]);

  const allItems = finalItems || defaultAllItems;

  const flattenAdjustments = (
    flattenedList: BillEstimateOrderItemDto['items'][0]['adjustments'],
    item: BillEstimateOrderItemDto['items'][0]
  ) => {
    const allAdjustmentsForItem = item.adjustments;

    if (allAdjustmentsForItem) {
      flattenedList = flattenedList.concat(allAdjustmentsForItem);
    }
    return flattenedList;
  };

  const allDiscountAdjustments = useMemo(() => allItems.reduce(flattenAdjustments, []), [allItems]);

  const grandTotal = useMemo(() => {
    const result = finalEstimates.reduce((sum, currentItem) => (sum += currentItem.grandTotal), 0);
    setGrandTotal && setGrandTotal(result);
    return result;
  }, [finalEstimates]);

  const taxes = useMemo(() => finalEstimates.reduce((sum, currentItem) => (sum += currentItem.totalTax), 0), [
    finalEstimates,
  ]);

  const { credits, discount, subTotal } = useMemo(() => {
    if (isLoading || !billEstimate) {
      return {
        credits: 0,
        discount: 0,
        subTotal: 0,
      };
    }

    const creditItems = allItems.filter((item) => item.total < 0);

    const credits = creditItems.reduce((sum, currentItem) => {
      const creditAmount = currentItem.total * -1;
      sum += creditAmount;

      return sum;
    }, 0);

    const subTotalItems = allItems.filter((item) => item.subtotal > 0);
    const subTotal = subTotalItems.reduce((sum, currentItem) => (sum += currentItem.subtotal), 0);

    const discount = allDiscountAdjustments.reduce((sum, currentAdjustment) => {
      sum += currentAdjustment.amount;

      return sum;
    }, 0);

    return {
      credits,
      discount,
      subTotal,
    };
  }, [allDiscountAdjustments, allItems, billEstimate, isLoading]);

  const totalDstFee = useMemo(() => {
    return dstItems.reduce((acc, curr) => acc + curr.subtotal, 0);
  }, [dstItems]);

  if (isLoading) {
    return (
      <SkeletonLoaderWrapper>
        <SkeletonLoader width="200px" />
        <br />
        <SkeletonLoader width="200px" />
      </SkeletonLoaderWrapper>
    );
  }

  interface TaxRates {
    taxLabel: string;
    tax: number;
  }

  const getTaxRates = (items: BillEstimateOrderItemDto['items'], paymentCurrency: string | null) => {
    const taxSummaryItems: TaxRates[] = [];
    items?.forEach((item: any) => {
      item?.taxItems?.forEach((taxItem: TaxItems) => {
        const taxSummaryKey = toNumber(taxItem.taxPercent) + '% ' + taxItem.taxAmountLabel;
        const existingSummaryItem = taxSummaryItems.find(
          (taxSummaryItem: TaxRates) => taxSummaryItem.taxLabel === taxSummaryKey
        );
        if (existingSummaryItem) {
          existingSummaryItem.tax += toNumber(taxItem.tax);
        } else {
          taxSummaryItems.push({
            taxLabel: taxSummaryKey,
            tax: toNumber(taxItem.tax),
          });
        }
      });
    });

    return taxSummaryItems?.length > 0 ? (
      <>
        {taxSummaryItems.map((item: TaxRates, index: number) => {
          return (
            <div className="tax-value" key={index}>
              {item.taxLabel}: <FormattedCurrencyV2 showCurrencyCode currency={paymentCurrency} value={item.tax} />
            </div>
          );
        })}
      </>
    ) : null;
  };

  const renderPrimitiveTooltip = (margin: string) => {
    return styled(TooltipPrimitive)`
      background: #172b4d;
      border-radius: 3px;
      color: #fff;
      padding: 2px 6px;
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      margin-right: ${margin};
    `;
  };

  const taxRates = getTaxRates([...allItems, ...dstItems], billEstimate?.currency ?? Currency.USD);

  const getDstLineItem = () => {
    if (!dstItems?.length) return null;
    return (
      <>
        <div className="price-grid-item price-grid-item__label">Administration Fee (DST)</div>
        <div className="price-grid-item price-grid-item__value">{formatToUSD(totalDstFee)}</div>
      </>
    );
  };

  return (
    <>
      {!renderDetailsAccordion && (
        <p className="old-price" aria-label="Current grand total of bill estimate">
          {formatToUSD(currentEstimatedBill)}
        </p>
      )}
      <p data-testid="new-price" aria-label="Updated grand total of bill estimate">
        {formatToUSD(grandTotal)}
      </p>

      <div
        className={classNames('price-grid', {
          'collapsed-accordion': renderDetailsAccordion,
          'expanded-accordion': showDetails,
        })}
      >
        <div className="price-grid-item price-grid-item__label">Subtotal</div>
        <div className="price-grid-item price-grid-item__value">{formatToUSD(subTotal)}</div>

        <div className="price-grid-item price-grid-item__label">Credits</div>
        <div className="price-grid-item price-grid-item__value">{formatToUSD(credits)}</div>

        <div className="price-grid-item price-grid-item__label">Discounts</div>
        <div className="price-grid-item price-grid-item__value">{formatToUSD(discount)}</div>

        {getDstLineItem()}

        <div className="price-grid-item price-grid-item__label">
          Taxes
          {taxRates && (
            <span data-testid="tax-tooltip" className="tax-tooltip">
              <Tooltip content={taxRates} position="left" tag="span" component={renderPrimitiveTooltip('35px')}>
                <InfoIcon size="small" label="tax-tooltip-info" />
              </Tooltip>
            </span>
          )}
        </div>
        <div className="price-grid-item price-grid-item__value">{formatToUSD(taxes ?? 0)}</div>
      </div>

      {renderDetailsAccordion && (
        <Button
          appearance="link"
          testId="bill-estimate-details"
          onClick={(e) => {
            e.stopPropagation();
            setShowDetails(!showDetails);
          }}
        >
          {!showDetails ? 'View details' : 'Hide details'}
        </Button>
      )}
    </>
  );
};
